import { Fragment, useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionFieldType,
  FieldImperativeHandle,
} from "@/components/@types";
import { FieldDefinitionAddressType } from "@/components/@types";
import { FieldTypography } from "@/components/organisms/Field/FieldTypography";
import { Skeleton } from "@/components/ui/skeleton";
import { typographyStyles } from "@/components/ui/typography";

function FieldAddress({
  label,
  value,
  onBlur,
  onChange,
  disabled,
  inputRef,
}: {
  label: string;
  value?: string;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  disabled: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div
      style={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          ...(typographyStyles["body-xs"] as React.CSSProperties),
          textTransform: "capitalize",
          color:
            "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
          padding: `0 ${"4px"}`,
          marginBottom: "1px",
        }}
      >
        {label}
      </div>
      <input
        ref={inputRef}
        type="text"
        value={value || ""}
        disabled={disabled}
        onBlur={onBlur}
        onChange={handleChange}
        data-testid={`field-address-${label.toLowerCase().replace(/\s+/g, "-")}`}
        style={{
          ...(typographyStyles["body-md"] as React.CSSProperties),
          borderRadius: "0.25rem",
          padding: "4px",
          border: `1px solid ${"var(--border)"}`,
          backgroundColor: "var(--background)",
          color: "var(--foreground)",
          width: "100%",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
          outline: "none",
          ...(disabled && {
            backgroundColor: "var(--muted)",
            color:
              "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
          }),
        }}
      />
    </div>
  );
}

const AddressRow = ({
  fields,
  onBlur,
  firstInputRef,
}: {
  fields: {
    label: string;
    value?: string;
    onChange?: (value: string) => void;
    disabled: boolean;
  }[];
  onBlur?: () => void;
  firstInputRef?: React.RefObject<HTMLInputElement>;
}) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        width: "100%",
        padding: `0 ${"8px"}`,
        gap: "8px",
      }}
    >
      {fields.map((field, index) => (
        <Fragment key={field.label}>
          <FieldAddress
            {...field}
            onBlur={onBlur}
            inputRef={index === 0 ? firstInputRef : undefined}
          />
        </Fragment>
      ))}
    </div>
  );
};

function AddressValue<T extends FieldDefinitionAddressType<T>>({
  events,
  value,
  size,
  isMultiline,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionAddressType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const firstInputRef = useRef<HTMLInputElement>(null);
  const valueAsString = [
    value?.addressLine1,
    value?.city,
    value?.region,
    value?.country,
    value?.postalCode,
  ]
    .filter((v) => v)
    .join(", ");

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        if (isMultiline) {
          firstInputRef.current?.focus();
        }
      },
      unsetFocus: () => {
        if (isMultiline) {
          firstInputRef.current?.blur();
        }
      },
    }),
    [isMultiline],
  );

  const getOnChangeForField =
    (field: keyof ReturnType<FieldDefinitionAddressType["getValue"]>) =>
    (newValue: string) => {
      events?.onChangeField?.({
        ...value,
        [field]: newValue,
      });
    };

  if (!isMultiline) {
    return (
      <FieldTypography
        value={valueAsString}
        events={events}
        isMultiline={false}
        size={size}
        ref={ref}
      />
    );
  } else {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
          gap: "8px",
          paddingTop: "8px",
          paddingBottom: "12px",
        }}
      >
        <AddressRow
          fields={[
            {
              label: "Address Line 1",
              value: value?.addressLine1,
              onChange: getOnChangeForField("addressLine1"),
              disabled: !events?.onChangeField,
            },
            {
              label: "Address Line 2",
              value: value?.addressLine2,
              onChange: getOnChangeForField("addressLine2"),
              disabled: !events?.onChangeField,
            },
          ]}
          onBlur={events?.onSubmitField}
          firstInputRef={firstInputRef}
        />
        <AddressRow
          fields={[
            {
              label: "City",
              value: value?.city,
              onChange: getOnChangeForField("city"),
              disabled: !events?.onChangeField,
            },
            {
              label: "Region",
              value: value?.region,
              onChange: getOnChangeForField("region"),
              disabled: !events?.onChangeField,
            },
          ]}
          onBlur={events?.onSubmitField}
        />
        <AddressRow
          fields={[
            {
              label: "Country",
              value: value?.country,
              onChange: getOnChangeForField("country"),
              disabled: !events?.onChangeField,
            },
            {
              label: "Postal Code",
              value: value?.postalCode,
              onChange: getOnChangeForField("postalCode"),
              disabled: !events?.onChangeField,
            },
          ]}
          onBlur={events?.onSubmitField}
        />
      </div>
    );
  }
}

/** An address loading in the shape it is drawn in: one line, as a list's cell
 * draws it; or, multiline, the panel's form of its parts. */
function AddressSkeleton({ isMultiLine }: { isMultiLine?: boolean }) {
  if (!isMultiLine) {
    return (
      <Skeleton
        variant="text"
        level="body-md"
        style={{
          width: "100%",
          minWidth: 0,
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      />
    );
  } else {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: `${"8px"} 0 ${"12px"} 0`,
        }}
      >
        {[1, 2, 3].map((rowIndex) => (
          <div
            key={rowIndex}
            style={{
              display: "flex",
              gap: "8px",
              padding: `0 ${"8px"}`,
            }}
          >
            {[1, 2].map((fieldIndex) => (
              <div
                key={fieldIndex}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "1px",
                }}
              >
                <Skeleton
                  variant="text"
                  level="body-xs"
                  style={{
                    width: 60,
                    height: 12,
                  }}
                />
                <Skeleton
                  variant="text"
                  level="body-md"
                  style={{
                    width: "100%",
                    height: 32,
                    borderRadius: "0.25rem",
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
}

export function Address<T extends FieldDefinitionAddressType<T>>({
  isLoading,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionAddressType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <AddressSkeleton isMultiLine={props.isMultiline} />;
  }

  return <AddressValue {...props} ref={ref} />;
}
